import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Send, CheckCircle2, Clock, Package, X, Inbox, ChevronRight, CheckSquare, Square } from "lucide-react";
import { toast } from "sonner";

const STATUS_FLOW = ["pagato_da_rivenditore", "confermato", "in_produzione", "spedito", "consegnato"];
const STATUS_LABELS = {
  pagato_da_rivenditore: { label: "In arrivo (pagato Riv)", color: "bg-blue-100 text-blue-800", icon: Send },
  confermato: { label: "Confermato", color: "bg-emerald-100 text-emerald-800", icon: CheckCircle2 },
  in_produzione: { label: "In produzione", color: "bg-amber-100 text-amber-800", icon: Clock },
  spedito: { label: "Spedito", color: "bg-violet-100 text-violet-800", icon: Package },
  consegnato: { label: "Consegnato", color: "bg-emerald-200 text-emerald-900", icon: CheckCircle2 },
  annullato: { label: "Annullato", color: "bg-red-100 text-red-800", icon: X },
};

export default function CasaMadrePoInbox() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState("all");
  const [expanded, setExpanded] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const load = async () => {
    const r = await api.get("/b2b/orders");
    setOrders(r.data || []);
  };
  useEffect(() => { load(); }, []);

  const advance = async (po, target, note = "") => {
    try {
      await api.put(`/b2b/orders/${po.id}/status`, { status: target, note });
      toast.success(`PO ${po.numero_ordine} → ${STATUS_LABELS[target]?.label || target}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  const filtered = filter === "all" ? orders : orders.filter((o) => o.status === filter);
  const counts = orders.reduce((acc, o) => { acc[o.status] = (acc[o.status] || 0) + 1; return acc; }, {});

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllVisible = () => {
    const visibleIds = filtered.filter((o) => o.status !== "annullato" && o.status !== "consegnato").map((o) => o.id);
    const allSelected = visibleIds.every((id) => selectedIds.has(id));
    setSelectedIds(allSelected ? new Set() : new Set(visibleIds));
  };

  const bulkAction = async (mode, target = null) => {
    if (selectedIds.size === 0) { toast.error("Nessun PO selezionato"); return; }
    const ids = Array.from(selectedIds);
    const confirmMsg = mode === "advance"
      ? `Avanzare ${ids.length} PO al prossimo stato del flusso?`
      : `Impostare ${ids.length} PO a stato "${STATUS_LABELS[target]?.label || target}"?`;
    if (!window.confirm(confirmMsg)) return;
    try {
      const body = mode === "advance"
        ? { po_ids: ids, advance: true, note: "Avanzamento bulk" }
        : { po_ids: ids, status: target, note: "Bulk update" };
      const r = await api.put("/b2b/orders/bulk-status", body);
      toast.success(`${r.data.updated_count} PO aggiornati${r.data.skipped_count ? ` · ${r.data.skipped_count} saltati` : ""}`);
      setSelectedIds(new Set());
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore bulk"); }
  };

  const visibleSelectables = filtered.filter((o) => o.status !== "annullato" && o.status !== "consegnato");
  const allVisibleSelected = visibleSelectables.length > 0 && visibleSelectables.every((o) => selectedIds.has(o.id));

  return (
    <AdminLayout>
      <div className="p-6 lg:p-8" data-testid="cm-po-inbox">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <div className="overline text-brand mb-1">Casa Madre · B2B</div>
            <h1 className="font-heading font-bold text-3xl flex items-center gap-2"><Inbox className="w-7 h-7" />PO in arrivo dai Rivenditori</h1>
            <p className="text-sm text-muted-foreground mt-1">Conferma, avanza in produzione e tracking. Un click per stato.</p>
          </div>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="h-10 w-60 rounded-sm" data-testid="po-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti ({orders.length})</SelectItem>
              {Object.entries(STATUS_LABELS).map(([k, v]) => (
                <SelectItem key={k} value={k}>{v.label} ({counts[k] || 0})</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-6">
          {STATUS_FLOW.map((s) => {
            const cfg = STATUS_LABELS[s];
            const Icon = cfg.icon;
            return (
              <div key={s} className={`p-3 rounded-sm ${cfg.color}`} data-testid={`stat-${s}`}>
                <div className="flex items-center gap-1.5 mb-1"><Icon className="w-3.5 h-3.5" /><span className="text-[10px] uppercase tracking-wider font-medium">{cfg.label}</span></div>
                <div className="font-heading font-bold text-2xl">{counts[s] || 0}</div>
              </div>
            );
          })}
        </div>

        {/* Bulk toolbar */}
        <div className="bg-white border border-border rounded-sm p-3 mb-3 flex items-center justify-between gap-3 flex-wrap" data-testid="po-bulk-toolbar">
          <button onClick={selectAllVisible} data-testid="po-select-all"
            className="inline-flex items-center gap-2 text-sm font-medium hover:opacity-80">
            {allVisibleSelected ? <CheckSquare className="w-4 h-4 text-brand" /> : <Square className="w-4 h-4" />}
            {allVisibleSelected ? "Deseleziona tutti" : "Seleziona tutti i selezionabili"}
          </button>
          <div className="text-xs text-muted-foreground">{selectedIds.size > 0 ? `${selectedIds.size} PO selezionati` : "Nessun PO selezionato"}</div>
          <div className="flex items-center gap-1">
            <button onClick={() => bulkAction("advance")} disabled={selectedIds.size === 0} data-testid="po-bulk-advance"
              className="h-9 px-3 rounded-sm bg-foreground text-white text-xs font-medium hover:bg-brand disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5">
              <ChevronRight className="w-3.5 h-3.5" /> Avanza {selectedIds.size > 0 ? `${selectedIds.size} ` : ""}PO al prossimo stato
            </button>
            <Select onValueChange={(v) => bulkAction("set", v)}>
              <SelectTrigger className="h-9 w-44 rounded-sm text-xs" data-testid="po-bulk-set-status" disabled={selectedIds.size === 0}>
                <SelectValue placeholder="Imposta stato..." />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_LABELS).filter(([k]) => k !== "pagato_da_rivenditore").map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* PO list */}
        <div className="space-y-2">
          {filtered.length === 0 ? (
            <div className="text-center text-muted-foreground py-16 border border-dashed border-border rounded-sm">Nessun PO nello stato selezionato</div>
          ) : filtered.map((po) => {
            const isExpanded = expanded === po.id;
            const statusCfg = STATUS_LABELS[po.status] || { label: po.status, color: "bg-neutral-100", icon: Clock };
            const StatusIcon = statusCfg.icon;
            const currentIdx = STATUS_FLOW.indexOf(po.status);
            const nextStatus = currentIdx >= 0 && currentIdx < STATUS_FLOW.length - 1 ? STATUS_FLOW[currentIdx + 1] : null;
            return (
              <div key={po.id} className={`bg-white border rounded-sm overflow-hidden ${selectedIds.has(po.id) ? "border-brand ring-1 ring-brand/30" : "border-border"}`} data-testid={`po-${po.numero_ordine}`}>
                <div className="p-3 flex items-center gap-3 hover:bg-accent/30 cursor-pointer" onClick={() => setExpanded(isExpanded ? null : po.id)}>
                  {po.status !== "annullato" && po.status !== "consegnato" ? (
                    <button onClick={(e) => { e.stopPropagation(); toggleSelect(po.id); }} data-testid={`po-select-${po.numero_ordine}`}
                      className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      {selectedIds.has(po.id) ? <CheckSquare className="w-4 h-4 text-brand" /> : <Square className="w-4 h-4 text-muted-foreground" />}
                    </button>
                  ) : <div className="w-5 h-5 flex-shrink-0" />}
                  <ChevronRight className={`w-4 h-4 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                  <div className="flex-1 grid grid-cols-5 gap-3 items-center">
                    <div>
                      <div className="font-mono text-sm">{po.numero_ordine}</div>
                      <div className="text-[10px] text-muted-foreground">{new Date(po.created_at).toLocaleString()}</div>
                    </div>
                    <div className="font-medium text-sm">{po.rivenditore_name}</div>
                    <div className="text-sm">{po.items?.length || 0} articoli</div>
                    <div className="font-medium">€ {po.totale}</div>
                    <div><span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm inline-flex items-center gap-1 ${statusCfg.color}`}><StatusIcon className="w-3 h-3" />{statusCfg.label}</span></div>
                  </div>
                  <div className="flex items-center gap-1">
                    {nextStatus && (
                      <button onClick={(e) => { e.stopPropagation(); advance(po, nextStatus); }} data-testid={`advance-${po.numero_ordine}`}
                        className="h-8 px-3 rounded-sm bg-foreground text-white text-xs hover:bg-brand transition-colors inline-flex items-center gap-1">
                        Avanza → {STATUS_LABELS[nextStatus]?.label}
                      </button>
                    )}
                    {po.status !== "annullato" && po.status !== "consegnato" && (
                      <button onClick={(e) => { e.stopPropagation(); if (window.confirm("Annullare il PO?")) advance(po, "annullato", "Annullato manualmente"); }}
                        data-testid={`cancel-${po.numero_ordine}`}
                        className="h-8 px-3 rounded-sm border border-red-300 text-red-600 text-xs hover:bg-red-50 inline-flex items-center gap-1">
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
                {isExpanded && (
                  <div className="px-6 pb-4 pt-1 bg-neutral-50 border-t border-border">
                    {po.note && <div className="text-xs italic text-muted-foreground mb-3">Note: &quot;{po.note}&quot;</div>}
                    <div className="space-y-1">
                      <div className="grid grid-cols-6 gap-2 text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border pb-1.5">
                        <div className="col-span-2">Articolo</div><div>Combinazione</div><div className="text-center">Qty</div><div className="text-right">€/cad</div><div className="text-right">Totale</div>
                      </div>
                      {po.items?.map((it, i) => (
                        <div key={i} className="grid grid-cols-6 gap-2 text-sm py-1 border-b border-border/50 last:border-0">
                          <div className="col-span-2">
                            <div className="font-medium">{it.nome}</div>
                            <div className="text-[10px] font-mono text-muted-foreground">{it.sku_variante}</div>
                          </div>
                          <div className="text-xs flex flex-wrap gap-1">
                            {Object.entries(it.combination || {}).map(([k, v]) => <span key={k} className="px-1.5 py-0.5 bg-white rounded-sm">{v}</span>)}
                          </div>
                          <div className="text-center">{it.quantita}</div>
                          <div className="text-right">€ {it.prezzo_unitario}</div>
                          <div className="text-right font-medium">€ {it.totale_riga}</div>
                        </div>
                      ))}
                    </div>
                    {po.status_history?.length > 0 && (
                      <div className="mt-4 pt-3 border-t border-border">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Storico stato</div>
                        <div className="space-y-1">
                          {po.status_history.map((h, i) => (
                            <div key={i} className="text-xs flex items-center gap-2">
                              <span className="text-muted-foreground">{new Date(h.timestamp).toLocaleString()}</span>
                              <span className="font-medium">→ {STATUS_LABELS[h.status]?.label || h.status}</span>
                              {h.user_email && <span className="text-muted-foreground">({h.user_email})</span>}
                              {h.note && <span className="italic text-muted-foreground">— {h.note}</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </AdminLayout>
  );
}
